export type ConfirmActionOptions = {
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red confirm button for actions that delete or cannot be undone. */
  destructive?: boolean;
};

type ConfirmActionRequest = ConfirmActionOptions & { id: number };

type ConfirmActionState = { request: ConfirmActionRequest | null; open: boolean };

let state: ConfirmActionState = { request: null, open: false };
let resolvePending: ((confirmed: boolean) => void) | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

const setState = (next: ConfirmActionState) => {
  state = next;
  listeners.forEach((listener) => listener());
};

/** Close the open confirmation and resolve its promise. Later calls for the same request do nothing. */
export const settleConfirmAction = (confirmed: boolean) => {
  const resolve = resolvePending;
  resolvePending = null;

  if (state.open) {
    // Keep the request so the modal content stays visible while it animates out.
    setState({ request: state.request, open: false });
  }

  resolve?.(confirmed);
};

/**
 * In-app replacement for `window.confirm`: an RTL AppModal (rendered by `ConfirmActionHost`) with a red confirm
 * button for destructive actions. Resolves `true` only when the user confirms;
 * closing the modal or opening another confirmation resolves `false`.
 */
export const confirmAction = (options: ConfirmActionOptions): Promise<boolean> => {
  settleConfirmAction(false);

  return new Promise<boolean>((resolve) => {
    resolvePending = resolve;
    setState({ request: { ...options, id: nextId++ }, open: true });
  });
};

export const subscribeToConfirmAction = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const getConfirmActionSnapshot = () => state;
